<template>
  <div>
    <page-content-toolbar
        projectName=""
        :title="(account && account.name) && $t('accountsAndBilling.account')+' '+account.name || $t('accountsAndBilling.createAccount')"
    >
    </page-content-toolbar>

    <v-card
      :loading="loading"
      class="mx-auto"
    >
      <template slot="progress">
        <v-progress-linear
          color="blue darken-4"
          height="2"
          indeterminate
        ></v-progress-linear>
      </template>

      <v-form v-model="valid">

        <v-card-text v-if="isCreationMode">
          <v-row>
            <v-col
              cols="6"
            >
              <v-text-field
                v-model="account.name"
                label="Account name"
                clearable
                maxlength="50"
                :rules="[rules.required]"
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-title v-if="!isCreationMode">{{ $t('accountsAndBilling.accountManagers') }}</v-card-title>

        <v-card-text v-if="!isCreationMode">
          
          <v-row>
            <v-col
              cols="6"
            >
              <v-select
                v-model="account.managers"
                :items="members"
                multiple
                chips
                clearable
                :item-text="memberFullName"
                :item-value="member"
              >
              </v-select>
            </v-col>
          </v-row>

        </v-card-text>


        <v-card-title v-if="!isCreationMode">{{ $t('accountsAndBilling.relatedProjects') }}</v-card-title>

        <v-card-text v-if="!isCreationMode">
          <v-row>
            <v-col
              cols="6"
            >
              <div class="text-left">
                <v-chip 
                  v-for="project in projects"
                  :key="project.id"
                  :value="project"
                  class="ma-2"
                  >
                  {{ project.name }}
                </v-chip>
              </div>
            </v-col>
          </v-row>
        </v-card-text>


        <v-card-title>{{ $t('accountsAndBilling.billingPlan') }}</v-card-title>

        <v-card-text>
          <v-row>
            <v-col
              cols="6"
            >
              <v-select
                v-model="account.billingPlan"
                :items="billingPlans"
                item-text="name"
                item-value="id"
                return-object
                append-outer-icon="mdi-plus"
                :rules="[rules.required]"
                :placeholder="$t('accountsAndBilling.chooseBillingPlan')"
                @click:append-outer="billingPlan = {name: '', billingFormula: '', requestUnitPrice: '0.0000',}; openBillingPlanDialog = true"
              >
                <template v-slot:item="{ item }">
                  {{ item.name }}
                  <v-spacer></v-spacer>
                  <v-btn
                    class="float-right"
                    icon
                    color="ternary"
                    @click="billingPlan = item; openBillingPlanDialog = true"
                  >
                    <v-icon>mdi-pencil-outline</v-icon>
                  </v-btn>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="primary"
            tile
            @click="saveAccount(account)"
            :loading="isSaving"
            :disabled="!account.billingPlan"
          >
            {{ isCreationMode ? $t('global.create') : $t('global.save') }}
          </v-btn>
          <v-btn
            color="ternary"
            dark
            tile
            @click="backToAccounts"
            :disabled="!account.billingPlan"
          >
            {{ $t('global.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>

    <billing-plan-dialog
      :open.sync="openBillingPlanDialog"
      :billingPlanEdited="billingPlan"
      :saveCallback="(billingPlan) => { if(billingPlan) { this.billingPlans.push(billingPlan); this.account.billingPlan = billingPlan; } }"
      :deleteCallback="() => { this.account.billingPlan = undefined; if(this.billingPlan) { const index = this.billingPlans.indexOf(this.billingPlan); if(index > -1) this.billingPlans.splice(index, 1); } }"
      v-on="$listeners"
    />
  </div>
</template>

<script>
import PageContentToolbar from '@/components/layout/PageContentToolbar';
import BillingPlanDialog from '@/components/accounts/BillingPlanDialog';

import accountService from '@/services/accounts.js';
import billingPlanService from '@/services/billingPlans.js';
import userRightMixin from '@/mixins/user-right-mixin.js';

import { testEmail } from '@/utils/utils.js';

export default {
  name: 'AccountDetails',

  components: { PageContentToolbar,  BillingPlanDialog, },

  mixins: [ userRightMixin ],

  data () {
    return {
      valid: false,
      isCreationMode: false,
      loading: true,
      dialog: false,
      openBillingPlanDialog: false,
      account: {
        name: null,
        managers: [],
        billingPlan: {
          name: null,
        }
      },
      members: [],
      projects: [],
      isSearchLoading: false,
      search: null,
      billingPlans: [],
      isUpdatingAccount: false,
      billingPlan: {
        name: 'A',
        billingFormula: 'A',
        requestUnitPrice: 0.01,
      },
      isSaving: false,
      rules: {
        required: value => !!value || this.$i18n.t('login.rules.required'),
        email: value => {
          return testEmail(value) || this.$i18n.t('login.rules.invalidEmail');
        },
      },
    };
  },

  methods: {
    memberFullName: member => member.firstName + ' ' + member.lastName,
    member: member => member,

    saveAccount(account) {
      if(this.isSaving) return

      this.isSaving = true;
      accountService.saveAccount(account)
        .then((response) => {
          this.$emit('raison-event', { type:'success', messages:[this.$t('accountsAndBilling.accountSaved')] });
          this.account = response.data;
          this.isCreationMode = false;
        })
        .catch(err => {
          this.$emit('raison-event', { type:'error', messages:[err] });
        })
        .finally(() => {
          this.isSaving = false;
        });
    },

    backToAccounts() {
      return this.$router.push({name: 'accounts'});
    },

    initializeUpdateMode(accountId) {
      this.loading = true;
      Promise.all([
        accountService.getAccount(accountId),
        accountService.getAccountMembers(accountId),
        accountService.getAccountProjects(accountId),
        billingPlanService.getBillingPlans()
      ]).then((responses) => {
        this.account = responses[0].data;
        this.members = responses[1].data;
        this.projects = responses[2].data;
        this.billingPlans = responses[3].data;
      }).catch(err => {
        this.$emit('raison-event', { type:'error', messages:[err] });
      }).finally(() => {
        this.loading = false;
      });
    },

    initializeCreationMode() {
      this.loading = true;
      billingPlanService.getBillingPlans()
        .then((response) => {
          if(response.data.length > 0) {
            this.billingPlans = response.data;
            this.account.billingPlan = Object.assign({}, this.billingPlans[0]);
          }
        }).catch(err => {
          this.$emit('raison-event', { type: 'error', messages: [err] });
        }).finally(() => {
          this.loading = false;
        });
    },

  },

  computed: {
    hasManagers() {
      return this.account.managers.length > 0
    }
  },

  created () {
    let accountId = this.$route.params.accountId;
    this.isCreationMode = !accountId;
    if(this.isCreationMode) {
      this.initializeCreationMode();
    }
    else {
      this.initializeUpdateMode(accountId);
    }
   },

};
</script>

<style>
</style>
